import React, { useCallback, useRef, useState } from "react";
import { IconCopy, IconTrash } from "@tabler/icons-react";
import { RiLoopRightLine } from "@remixicon/react";

// Shared exact text height measurement matching non-edit rendering styles
const measureTextHeightExact = (
  text: string,
  widthPx: number,
  fontSizePx: number,
  fontFamilyName: string,
  fontWeight: "400" | "700" = "400",
  fontStyle: "normal" | "italic" = "normal",
): number => {
  const el = document.createElement("span");
  el.style.position = "absolute";
  el.style.visibility = "hidden";
  el.style.whiteSpace = "pre-wrap";
  el.style.wordBreak = "break-word";
  el.style.lineHeight = "1.2";
  el.style.fontSize = `${fontSizePx}px`;
  el.style.fontFamily = fontFamilyName;
  el.style.fontWeight = fontWeight;
  el.style.fontStyle = fontStyle;
  el.style.display = "block";
  el.style.width = `${Math.max(1, widthPx)}px`;
  el.style.margin = "0";
  el.style.padding = "0";
  const normalizedText = text && text.endsWith("\n") ? `${text}\u00A0` : text || "M";
  el.textContent = normalizedText;
  document.body.appendChild(el);
  const h = el.getBoundingClientRect().height;
  document.body.removeChild(el);
  return h;
};

// Calculate rotated rectangle corners for rotation-aware snapping
const getRotatedCorners = (
  x: number,
  y: number,
  width: number,
  height: number,
  rotationDeg: number,
) => {
  const centerX = x + width / 2;
  const centerY = y + height / 2;
  const rotationRad = (rotationDeg * Math.PI) / 180;

  // Calculate the four corners relative to center
  const corners = [
    { x: -width / 2, y: -height / 2 }, // top-left
    { x: width / 2, y: -height / 2 }, // top-right
    { x: width / 2, y: height / 2 }, // bottom-right
    { x: -width / 2, y: height / 2 }, // bottom-left
  ];

  // Rotate each corner around the center
  const rotatedCorners = corners.map((corner) => {
    const cos = Math.cos(rotationRad);
    const sin = Math.sin(rotationRad);
    return {
      x: centerX + corner.x * cos - corner.y * sin,
      y: centerY + corner.x * sin + corner.y * cos,
    };
  });

  return rotatedCorners;
};

// Get the extreme points for rotation-aware snapping
const getRotatedExtremes = (
  x: number,
  y: number,
  width: number,
  height: number,
  rotationDeg: number,
) => {
  const corners = getRotatedCorners(x, y, width, height, rotationDeg);

  // Find the extreme points
  const leftmost = Math.min(...corners.map((c) => c.x));
  const rightmost = Math.max(...corners.map((c) => c.x));
  const topmost = Math.min(...corners.map((c) => c.y));
  const bottommost = Math.max(...corners.map((c) => c.y));

  return {
    left: leftmost,
    right: rightmost,
    top: topmost,
    bottom: bottommost,
    centerX: x + width / 2,
    centerY: y + height / 2,
  };
};

interface TextBox {
  id: string;
  x: number;
  y: number;
  text: string;
  fontSize: number;
  fontFamily: string;
  fill: string;
  width: number;
  rotation: number;
  // text styles
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isStrikethrough: boolean;
  textAlign: "left" | "center" | "right";
  isSelected: boolean;
  isEditing: boolean;
}

interface KonvaTextBoxProps {
  imageRef: React.RefObject<HTMLImageElement>;
  onTextBoxesChange?: (textBoxes: TextBox[]) => void;
  onSelectionChange?: (selected: TextBox | null) => void;
  isAddingText?: boolean;
  onToggleAddingText?: () => void;
  addNowToken?: number; // increments to request immediate add
  formattingRequest?: { token: number; updates: Partial<TextBox> };
  deselectRequestToken?: number; // increments to request deselect all
  // Notifies parent when any textbox begins or ends a drag/resize interaction
  onInteractionStateChange?: (isActive: boolean) => void;
  // Initial text boxes to load when the component mounts (e.g., per-image configs)
  initialTextBoxes?: TextBox[];
  // Default font settings for new text boxes
  defaultFont?: {
    fontFamily: string;
    fontSize: number;
    fill: string;
  };
  // Peer image boxes for cross-snapping
  peerImageBoxes?: Array<{
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation?: number;
  }>;
}

const KonvaTextBox: React.FC<KonvaTextBoxProps> = ({
  imageRef,
  onTextBoxesChange,
  onSelectionChange,
  isAddingText = false,
  onToggleAddingText,
  addNowToken,
  formattingRequest,
  deselectRequestToken,
  onInteractionStateChange,
  initialTextBoxes,
  defaultFont,
  peerImageBoxes,
}) => {
  const [textBoxes, setTextBoxes] = useState<TextBox[]>(initialTextBoxes || []);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [clipRect, setClipRect] = useState<{
    left: number;
    top: number;
    clientLeft: number;
    clientTop: number;
    width: number;
    height: number;
  }>({ left: 0, top: 0, clientLeft: 0, clientTop: 0, width: 0, height: 0 });
  const [clipBorderRadius, setClipBorderRadius] = useState<string>("0px");
  // Suppress overlay click after drag/resize to avoid unintended deselect
  const suppressNextOverlayClickRef = useRef<boolean>(false);
  // Live measurement of rendered content heights for perfect sync with wrapping
  const contentRefMap = useRef<Map<string, HTMLElement>>(new Map());
  const [measuredOuterHeights, setMeasuredOuterHeights] = useState<
    Record<string, number>
  >({});
  // Track if any text box is currently being dragged/resized so we can
  // globally suppress hover/deselect behavior and pointer events on others
  const [interactingBoxId, setInteractingBoxId] = useState<string | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const refCallbackMapRef = useRef<Map<string, (el: HTMLElement | null) => void>>(
    new Map(),
  );
  // Active guide lines while dragging
  const [activeGuideV, setActiveGuideV] = useState<{
    x: number;
    kind: "container" | "peer";
  } | null>(null);
  const [activeGuideH, setActiveGuideH] = useState<{
    y: number;
    kind: "container" | "peer";
  } | null>(null);

  // removed: local measureTextHeightExact (now using shared top-level function)

  // Compute image bounds relative to overlay for clipping
  React.useEffect(() => {
    const computeClip = () => {
      const img = imageRef?.current;
      const overlay = overlayRef.current;
      if (!img || !overlay) return;

      // Use layout offsets relative to the shared container to avoid rect rounding/baseline gaps
      const left = img.offsetLeft;
      const top = img.offsetTop;
      const clientLeft = img.getBoundingClientRect().left;
      const clientTop = img.getBoundingClientRect().top;
      const width = img.offsetWidth;
      const height = img.offsetHeight;

      setClipRect({ left, top, clientLeft, clientTop, width, height });

      // Apply same border radius as container for consistent clipping at corners
      const container = overlay.parentElement;
      if (container) {
        const cs = getComputedStyle(container);
        setClipBorderRadius(cs.borderRadius || "0px");
      }
    };

    computeClip();
    const handleWindowResize = () => computeClip();
    window.addEventListener("resize", handleWindowResize);

    const img = imageRef?.current;
    if (img) img.addEventListener("load", computeClip);

    // Observe size changes of image and overlay for perfect sync
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => computeClip());
      if (img) ro.observe(img);
      if (overlayRef.current) ro.observe(overlayRef.current);
    }

    return () => {
      window.removeEventListener("resize", handleWindowResize);
      if (img) img.removeEventListener("load", computeClip);
      if (ro) ro.disconnect();
    };
  }, [imageRef]);

  // Setup ResizeObserver to keep measuredOuterHeights in sync with actual rendered content
  React.useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    if (!resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver((entries) => {
        const updates: Record<string, number> = {};
        for (const entry of entries) {
          const target = entry.target as HTMLElement;
          const measured =
            entry.contentRect && entry.contentRect.height
              ? entry.contentRect.height
              : target.getBoundingClientRect().height;
          for (const [id, el] of contentRefMap.current.entries()) {
            if (el === target) {
              updates[id] = measured;
              break;
            }
          }
        }
        if (Object.keys(updates).length > 0) {
          setMeasuredOuterHeights((prev) => {
            let changed = false;
            const next: Record<string, number> = { ...prev };
            for (const [k, v] of Object.entries(updates)) {
              const prevV = prev[k];
              if (typeof prevV !== "number" || Math.abs(prevV - v) > 0.5) {
                // epsilon to avoid thrash
                next[k] = v;
                changed = true;
              }
            }
            return changed ? next : prev;
          });
        }
      });
    }

    const ro = resizeObserverRef.current;
    const elements = Array.from(contentRefMap.current.values());
    elements.forEach((el) => ro.observe(el));

    return () => {
      if (ro) elements.forEach((el) => ro.unobserve(el));
    };
  }, [textBoxes]);

  // When initialTextBoxes prop changes (e.g., from edit_configuration hydration),
  // update internal state so boxes render without requiring an image switch.
  React.useEffect(() => {
    if (!initialTextBoxes) return;
    setTextBoxes(initialTextBoxes);
    setSelectedId(null);
  }, [initialTextBoxes]);

  const registerContentRef = useCallback((id: string, el: HTMLElement | null) => {
    const prev = contentRefMap.current.get(id) || null;
    if (prev === el) return;

    if (prev && resizeObserverRef.current) {
      resizeObserverRef.current.unobserve(prev);
    }
    if (el) {
      contentRefMap.current.set(id, el);
      if (resizeObserverRef.current) {
        resizeObserverRef.current.observe(el);
      }
      setMeasuredOuterHeights((prevHeights) => {
        const nextValue = el.getBoundingClientRect().height;
        const prevValue = prevHeights[id];
        if (typeof prevValue === "number" && Math.abs(prevValue - nextValue) <= 0.5)
          return prevHeights;
        return { ...prevHeights, [id]: nextValue };
      });
    } else {
      contentRefMap.current.delete(id);
    }
  }, []);

  const getContentRefCallback = useCallback(
    (id: string) => {
      const existing = refCallbackMapRef.current.get(id);
      if (existing) return existing;
      const fn = (el: HTMLElement | null) => registerContentRef(id, el);
      refCallbackMapRef.current.set(id, fn);
      return fn;
    },
    [registerContentRef],
  );

  // Track last processed addNowToken to avoid duplicate adds on layout changes
  const lastProcessedAddTokenRef = useRef<number>(0);

  // Generate unique ID for text boxes
  const generateId = () =>
    `textbox_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Update text box properties
  const updateTextBox = useCallback(
    (id: string, updates: Partial<TextBox>) => {
      setTextBoxes((prev) => {
        let updatedSelected: TextBox | null = null;
        const updated = prev.map((textBox) => {
          if (textBox.id === id) {
            const merged = { ...textBox, ...updates } as TextBox;
            if (merged.id === selectedId && merged.isSelected !== false) {
              updatedSelected = merged;
            }
            return merged;
          }
          return textBox;
        });
        onTextBoxesChange?.(updated);
        if (updatedSelected) {
          onSelectionChange?.(updatedSelected);
        }
        return updated;
      });
    },
    [onTextBoxesChange, onSelectionChange, selectedId],
  );

  // Add new text box
  const addTextBox = useCallback(
    (x: number, y: number) => {
      const newTextBox: TextBox = {
        id: generateId(),
        x,
        y,
        text: "Sample Text",
        fontSize: defaultFont?.fontSize || 24,
        fontFamily: defaultFont?.fontFamily || "Arial",
        fill: defaultFont?.fill || "#000000",
        width: 200,
        rotation: 0,
        isBold: false,
        isItalic: false,
        isUnderline: false,
        isStrikethrough: false,
        textAlign: "left",
        isSelected: true,
        isEditing: true,
      };

      setTextBoxes((prev) => {
        const cleared = prev.map((tb) => ({
          ...tb,
          isSelected: false,
          isEditing: false,
        }));
        const updated = [...cleared, newTextBox];
        onTextBoxesChange?.(updated);
        return updated;
      });
      setSelectedId(newTextBox.id);
      onSelectionChange?.(newTextBox);
    },
    [onTextBoxesChange, onSelectionChange, defaultFont],
  );

  // Process addNowToken once per token value
  React.useEffect(() => {
    if (!addNowToken) return;
    if (lastProcessedAddTokenRef.current === addNowToken) return;
    lastProcessedAddTokenRef.current = addNowToken;
    const { left, top, width, height } = clipRect;
    if (width === 0 || height === 0) return;
    const defaultWidth = 200;
    const x = left + width / 2 - defaultWidth / 2;
    const y = top + height / 2 - 14; // approx half line height at 24px
    addTextBox(x, y);
    onToggleAddingText?.();
  }, [addNowToken, clipRect, addTextBox, onToggleAddingText]);

  // Handle click on the overlay
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      // Ignore overlay click if it immediately follows a drag/resize interaction
      if (suppressNextOverlayClickRef.current) return;
      // Also ignore overlay clicks while any text box is interacting
      if (interactingBoxId) return;
      if (isAddingText) {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        addTextBox(x, y);
        onToggleAddingText?.();
      } else if (selectedId) {
        // Click outside to deselect
        setTextBoxes((prev) => {
          const updated = prev.map((tb) => ({
            ...tb,
            isSelected: false,
            isEditing: false,
          }));
          onTextBoxesChange?.(updated);
          return updated;
        });
        setSelectedId(null);
        onSelectionChange?.(null);
      }
    },
    [
      isAddingText,
      addTextBox,
      onToggleAddingText,
      selectedId,
      onSelectionChange,
      onTextBoxesChange,
      interactingBoxId,
    ],
  );

  // Additionally, swallow the very next overlay click after mouseup globally, regardless
  // of timing of React handlers, by listening at capture phase once.
  React.useEffect(() => {
    if (!interactingBoxId) return;
    const overlay = overlayRef.current;
    if (!overlay) return;
    const preventOnce = (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
    };
    const options: AddEventListenerOptions = { capture: true, once: true };
    overlay.addEventListener("click", preventOnce, options);
    return () =>
      overlay.removeEventListener("click", preventOnce, {
        capture: true,
      } as EventListenerOptions);
  }, [interactingBoxId]);

  // Apply formatting requests from parent to currently selected text box exactly once per token
  const lastFormattingTokenRef = useRef<number>(0);
  React.useEffect(() => {
    const token = formattingRequest?.token || 0;
    if (!token || token === lastFormattingTokenRef.current) return;
    lastFormattingTokenRef.current = token;
    const fallbackSelectedId =
      selectedId || (textBoxes.find((tb) => tb.isSelected)?.id ?? null);
    if (!fallbackSelectedId) return;
    updateTextBox(fallbackSelectedId, formattingRequest?.updates || {});
  }, [
    formattingRequest?.token,
    formattingRequest?.updates,
    selectedId,
    textBoxes,
    updateTextBox,
  ]);

  // Deselect all on parent request
  const lastDeselectTokenRef = useRef<number>(0);
  React.useEffect(() => {
    if (!deselectRequestToken) return;
    if (lastDeselectTokenRef.current === deselectRequestToken) return;
    lastDeselectTokenRef.current = deselectRequestToken;
    setTextBoxes((prev) => {
      const updated = prev.map((tb) => ({ ...tb, isSelected: false, isEditing: false }));
      onTextBoxesChange?.(updated);
      return updated;
    });
    setSelectedId(null);
    onSelectionChange?.(null);
  }, [deselectRequestToken, onTextBoxesChange, onSelectionChange]);

  // Keyboard shortcuts: Delete/Backspace to remove selected; Escape to deselect
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Use fallback to find selected text box if selectedId is null
      const fallbackSelectedId =
        selectedId || (textBoxes.find((tb) => tb.isSelected)?.id ?? null);
      if (!fallbackSelectedId) return;

      if (e.key === "Delete" || e.key === "Backspace") {
        const anyEditing = textBoxes.some((t) => t.isEditing);
        if (anyEditing) return;
        setTextBoxes((prev) => {
          const updated = prev.filter((tb) => tb.id !== fallbackSelectedId);
          onTextBoxesChange?.(updated);
          return updated;
        });
        setSelectedId(null);
        onSelectionChange?.(null);
      } else if (e.key === "Escape") {
        setTextBoxes((prev) =>
          prev.map((tb) => ({ ...tb, isSelected: false, isEditing: false })),
        );
        setSelectedId(null);
        onSelectionChange?.(null);
      } else if (
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight"
      ) {
        const anyEditing = textBoxes.some((t) => t.isEditing);
        if (anyEditing) return; // Don't move while editing text

        e.preventDefault(); // Prevent page scrolling

        // Movement distance: 1px for normal, 10px with Shift modifier
        const moveDistance = e.shiftKey ? 10 : 1;

        let deltaX = 0;
        let deltaY = 0;

        switch (e.key) {
          case "ArrowUp":
            deltaY = -moveDistance;
            break;
          case "ArrowDown":
            deltaY = moveDistance;
            break;
          case "ArrowLeft":
            deltaX = -moveDistance;
            break;
          case "ArrowRight":
            deltaX = moveDistance;
            break;
        }

        setTextBoxes((prev) => {
          const updated = prev.map((tb) =>
            tb.id === fallbackSelectedId
              ? { ...tb, x: tb.x + deltaX, y: tb.y + deltaY }
              : tb,
          );
          onTextBoxesChange?.(updated);
          return updated;
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId, textBoxes, onTextBoxesChange, onSelectionChange]);

  return (
    <div
      ref={overlayRef}
      className="absolute inset-0 pointer-events-auto"
      style={{ cursor: isAddingText ? "crosshair" : "default" }}
      onMouseDown={handleOverlayClick}
      data-text-overlay
    >
      {/* Alignment guides - vertical */}
      {activeGuideV && (
        <div
          style={{
            position: "absolute",
            left: `${activeGuideV.x}px`,
            top: `${clipRect.top}px`,
            height: `${clipRect.height}px`,
            width: 0,
            borderLeft:
              activeGuideV.kind === "container"
                ? "1px solid #7e1cff"
                : "1px dashed #7e1cff",
            zIndex: 60,
            pointerEvents: "none",
          }}
        />
      )}
      {/* Alignment guides - horizontal */}
      {activeGuideH && (
        <div
          style={{
            position: "absolute",
            top: `${activeGuideH.y}px`,
            left: `${clipRect.left}px`,
            width: `${clipRect.width}px`,
            height: 0,
            borderTop:
              activeGuideH.kind === "container"
                ? "1px solid #7e1cff"
                : "1px dashed #7e1cff",
            zIndex: 60,
            pointerEvents: "none",
          }}
        />
      )}
      {/* Render text boxes using HTML/CSS */}
      {textBoxes.map((textBox) => (
        <SimpleTextBox
          key={textBox.id}
          textBox={textBox}
          liveOuterHeight={measuredOuterHeights[textBox.id]}
          onUpdate={(updates) => updateTextBox(textBox.id, updates)}
          onSelect={() => {
            setTextBoxes((prev) => {
              const updated = prev.map((tb) =>
                tb.id === textBox.id
                  ? { ...tb, isSelected: true, isEditing: false }
                  : { ...tb, isSelected: false, isEditing: false },
              );
              onTextBoxesChange?.(updated);
              const selected = updated.find((tb) => tb.id === textBox.id) || null;
              onSelectionChange?.(selected);
              return updated;
            });
            setSelectedId(textBox.id);
          }}
          onDoubleClick={() => updateTextBox(textBox.id, { isEditing: true })}
          onInteractionStart={() => {
            suppressNextOverlayClickRef.current = true;
            setInteractingBoxId(textBox.id);
            onInteractionStateChange?.(true);
          }}
          onInteractionEnd={() => {
            // Keep interaction flag and suppression for a short time so late overlay clicks
            // or hover transitions do not cause deselect/hover flashes
            suppressNextOverlayClickRef.current = true;
            setTimeout(() => {
              setInteractingBoxId(null);
              suppressNextOverlayClickRef.current = false;
              onInteractionStateChange?.(false);
            }, 150);
          }}
          onDuplicate={() => {
            setTextBoxes((prev) => {
              const source = prev.find((tb) => tb.id === textBox.id);
              if (!source) return prev;
              const duplicated: TextBox = {
                ...source,
                id: generateId(),
                x: source.x + 20,
                y: source.y + 20,
                isSelected: true,
                isEditing: false,
              };
              const cleared = prev.map((tb) => ({
                ...tb,
                isSelected: false,
                isEditing: false,
              }));
              const updated = [...cleared, duplicated];
              onTextBoxesChange?.(updated);
              onSelectionChange?.(duplicated);
              setSelectedId(duplicated.id);
              return updated;
            });
            // hover state belongs to child; selecting the duplicated box replaces hover visuals
          }}
          onDelete={() => {
            setTextBoxes((prev) => {
              const updated = prev.filter((tb) => tb.id !== textBox.id);
              onTextBoxesChange?.(updated);
              onSelectionChange?.(null);
              setSelectedId(null);
              return updated;
            });
          }}
          isGloballyInteracting={Boolean(interactingBoxId)}
          activeInteractingId={interactingBoxId}
          clipRect={clipRect}
          peerRects={textBoxes
            .filter((tb) => tb.id !== textBox.id)
            .map((tb) => ({
              id: tb.id,
              x: tb.x,
              y: tb.y,
              width: tb.width,
              height: measuredOuterHeights[tb.id] || tb.fontSize * 1.2,
              rotation: tb.rotation,
            }))
            .concat(
              (peerImageBoxes || []).map((b) => ({
                id: b.id,
                x: b.x,
                y: b.y,
                width: b.width,
                height: b.height,
                rotation: b.rotation || 0,
              })),
            )}
          setGuides={(v, h) => {
            setActiveGuideV(v);
            setActiveGuideH(h);
          }}
        />
      ))}
      {/* Masked text layer: render text (and textarea) clipped to image bounds */}
      <div
        style={{
          boxSizing: "border-box",
          position: "absolute",
          left: `${clipRect.left}px`,
          top: `${clipRect.top}px`,
          width: `${clipRect.width}px`,
          height: `${clipRect.height}px`,
          overflow: "hidden",
          borderRadius: clipBorderRadius,
          zIndex: 10,
          pointerEvents: textBoxes.some((t) => t.isEditing) ? "auto" : "none",
        }}
      >
        {textBoxes.map((tb) => (
          <div
            key={`content_${tb.id}`}
            style={{
              position: "absolute",
              left: `${tb.x - clipRect.left}px`,
              top: `${tb.y - clipRect.top}px`,
              width: `${tb.width}px`,
              fontSize: `${tb.fontSize}px`,
              fontFamily: tb.fontFamily,
              color: tb.fill,
              fontWeight: tb.isBold ? "700" : "400",
              fontStyle: tb.isItalic ? "italic" : "normal",
              textDecoration:
                `${tb.isUnderline ? "underline " : ""}${tb.isStrikethrough ? " line-through" : ""}`.trim(),
              textAlign: tb.textAlign,
              transform: `rotate(${tb.rotation}deg)`,
              padding: "0px",
            }}
            ref={getContentRefCallback(tb.id)}
          >
            {/* Measurement/display span: always rendered for consistent height. Hidden during edit but occupies space */}
            <span
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                lineHeight: "1.2",
                display: "block",
                margin: 0,
                padding: 0,
                visibility: tb.isEditing ? "hidden" : "visible",
              }}
            >
              {tb.text.endsWith("\n") ? `${tb.text}\u00A0` : tb.text}
            </span>

            {/* Editing overlay: absolutely positioned, does not affect layout height */}
            {tb.isEditing && (
              <textarea
                autoFocus
                value={tb.text}
                onFocus={(e) => {
                  const el = e.currentTarget as HTMLTextAreaElement;
                  if (!el.dataset) return;
                  if (!("selectedOnce" in el.dataset)) {
                    el.select();
                    el.dataset.selectedOnce = "1";
                  }
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  updateTextBox(tb.id, { text: e.currentTarget.value });
                }}
                onBlur={() => {
                  updateTextBox(tb.id, { isEditing: false });
                }}
                style={
                  {
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    fontSize: "inherit",
                    fontFamily: "inherit",
                    fontWeight: tb.isBold ? "700" : "400",
                    fontStyle: tb.isItalic ? "italic" : "normal",
                    textDecoration:
                      `${tb.isUnderline ? "underline " : ""}${tb.isStrikethrough ? " line-through" : ""}`.trim(),
                    color: "inherit",
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    resize: "none",
                    padding: 0,
                    margin: 0,
                    lineHeight: "1.2",
                    overflow: "hidden",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    textAlign: tb.textAlign as React.CSSProperties["textAlign"],
                  } as React.CSSProperties
                }
                onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                  if (e.key === "Enter") {
                    // ensure newline-only addition increases height by one line
                    // the span controls height; just let the value include the newline
                    requestAnimationFrame(() => {
                      const styles = getComputedStyle(e.currentTarget);
                      const fontSizePx = parseInt(styles.fontSize);
                      const h = measureTextHeightExact(
                        e.currentTarget.value,
                        tb.width,
                        fontSizePx,
                        tb.fontFamily,
                      );
                      // Nudge layout without changing the outer box (span drives height)
                      e.currentTarget.style.minHeight = `${Math.max(30, h)}px`;
                    });
                  }
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Simple HTML/CSS TextBox component
interface SimpleTextBoxProps {
  textBox: TextBox;
  liveOuterHeight?: number;
  onUpdate: (updates: Partial<TextBox>) => void;
  onSelect: () => void;
  onDoubleClick: () => void;
  onInteractionStart: () => void;
  onInteractionEnd: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  // Global interaction flags so this box can mute hover when another box is active
  isGloballyInteracting: boolean;
  activeInteractingId: string | null;
  clipRect: {
    left: number;
    top: number;
    clientLeft: number;
    clientTop: number;
    width: number;
    height: number;
  };
  peerRects: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
    id: string;
    rotation?: number;
  }>;
  setGuides: (
    v: { x: number; kind: "container" | "peer" } | null,
    h: { y: number; kind: "container" | "peer" } | null,
  ) => void;
}

const SimpleTextBox: React.FC<SimpleTextBoxProps> = ({
  textBox,
  liveOuterHeight,
  onUpdate,
  onSelect,
  onDoubleClick,
  onInteractionStart,
  onInteractionEnd,
  onDuplicate,
  onDelete,
  isGloballyInteracting,
  activeInteractingId,
  clipRect,
  peerRects,
  setGuides,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [isResizing, setIsResizing] = useState<string | null>(null);
  const [isRotating, setIsRotating] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, startX: 0, startY: 0 });
  const [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    fontSize: 0,
    startX: 0,
    startY: 0,
    startContentHeight: 0,
  });
  const [rotationStart, setRotationStart] = useState({
    x: 0,
    y: 0,
    startRotation: 0,
    centerX: 0,
    centerY: 0,
    initialAngle: 0,
  });
  const [isHovered, setIsHovered] = useState(false);
  const wasDragRecentlyRef = useRef<boolean>(false);
  const leftRecentlyRef = useRef<boolean>(false);
  // When this box becomes deselected, clear hover outline immediately
  React.useEffect(() => {
    if (!textBox.isSelected && isHovered) {
      setIsHovered(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textBox.isSelected]);
  // If another box is interacting globally, suppress hover visuals for this box
  React.useEffect(() => {
    if (isGloballyInteracting && activeInteractingId !== textBox.id && isHovered) {
      setIsHovered(false);
    }
  }, [isGloballyInteracting, activeInteractingId, textBox.id, isHovered]);
  // Geometry constants for alignment
  const SELECT_OUTLINE_PX = 0; // matches outline thickness
  const CORNER_SIZE = 10; // px
  const SIDE_WIDTH = 8; // px
  const SIDE_HEIGHT = 16; // px
  const CORNER_OFFSET_PX = -Math.round(CORNER_SIZE / 2 + SELECT_OUTLINE_PX / 2);
  const SIDE_OFFSET_PX = -Math.round(SIDE_WIDTH / 2 + SELECT_OUTLINE_PX / 2);

  // Minimum size thresholds for showing resize handles
  const MIN_SIZE_FOR_CORNER_HANDLES = 30; // px - show corner handles when larger than this
  const MIN_SIZE_FOR_SIDE_HANDLES = 30; // px - show side handles when larger than this

  // Helper functions to determine handle visibility
  const shouldShowCornerHandles = () => {
    return (
      textBox.width >= MIN_SIZE_FOR_CORNER_HANDLES &&
      contentHeight >= MIN_SIZE_FOR_CORNER_HANDLES
    );
  };

  const shouldShowSideHandles = () => {
    return (
      textBox.width >= MIN_SIZE_FOR_SIDE_HANDLES &&
      contentHeight >= MIN_SIZE_FOR_SIDE_HANDLES
    );
  };

  // Measure rendered text height for given constraints (accounts for wrapping)
  const measureTextHeight = React.useCallback(
    (
      text: string,
      width: number,
      fontSize: number,
      fontFamily: string,
      isBold?: boolean,
      isItalic?: boolean,
    ) =>
      measureTextHeightExact(
        text,
        width,
        fontSize,
        fontFamily,
        isBold ? "700" : "400",
        isItalic ? "italic" : "normal",
      ),
    [],
  );

  // removed unused textarea handlers (text rendered in clipped layer)

  // removed unused textarea handlers

  // removed unused textarea handlers

  const handleClick = (e: React.MouseEvent) => {
    // Get click position relative to the overlay container
    const overlay = e.currentTarget.closest("[data-text-overlay]") as HTMLElement;
    if (!overlay) {
      e.stopPropagation();
      onSelect();
      return;
    }

    const overlayRect = overlay.getBoundingClientRect();
    const clickX = e.clientX - overlayRect.left;
    const clickY = e.clientY - overlayRect.top;
    const textBoxArea = textBox.width * contentHeight;

    // Check if any peer image boxes are smaller and under this click
    for (const peer of peerRects) {
      const peerArea = peer.width * peer.height;
      // If peer is significantly smaller (less than 70% of text box area)
      if (peerArea < textBoxArea * 0.7) {
        // Check if click is within the peer's bounds
        if (
          clickX >= peer.x &&
          clickX <= peer.x + peer.width &&
          clickY >= peer.y &&
          clickY <= peer.y + peer.height
        ) {
          // Find the image overlay (sibling of text overlay) and then the image box element
          const imageOverlay = overlay.parentElement?.querySelector(
            "[data-image-overlay]",
          ) as HTMLElement;
          const imageBoxElement = imageOverlay?.querySelector(
            `[data-image-box-id="${peer.id}"]`,
          ) as HTMLElement;
          if (imageBoxElement) {
            e.stopPropagation();
            // Create a new click event and dispatch it to the image box
            const syntheticClick = new MouseEvent("click", {
              bubbles: true,
              cancelable: true,
              clientX: e.clientX,
              clientY: e.clientY,
            });
            imageBoxElement.dispatchEvent(syntheticClick);
            return;
          }
        }
      }
    }

    e.stopPropagation();
    onSelect();
  };

  const lastDragEndedAtRef = useRef<number>(0);

  const handleDoubleClick = (e: React.MouseEvent) => {
    if (textBox.isEditing) return;
    e.stopPropagation();
    // Avoid entering edit mode only if a drag/resize is ongoing or just ended very recently
    if (isDragging || Boolean(isResizing)) return;
    if (wasDragRecentlyRef.current) return;
    if (leftRecentlyRef.current) return;
    onDoubleClick();
  };

  // Drag functionality
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Start drag and ensure box is selected when drag begins from hover
    onSelect();
    onInteractionStart();
    setIsDragging(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      startX: textBox.x,
      startY: textBox.y,
    });
  };

  // Resize handle functionality
  const handleResizeMouseDown = (e: React.MouseEvent, direction: string) => {
    e.preventDefault();
    e.stopPropagation();

    onInteractionStart();
    setIsResizing(direction);
    // Capture starting multi-line height using live measurement if available for perfect baseline
    const startContentHeight =
      typeof liveOuterHeight === "number"
        ? liveOuterHeight
        : measureTextHeight(
            textBox.text,
            textBox.width,
            textBox.fontSize,
            textBox.fontFamily,
            textBox.isBold,
            textBox.isItalic,
          );
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: textBox.width,
      height: 30, // Min height
      fontSize: textBox.fontSize,
      startX: textBox.x,
      startY: textBox.y,
      startContentHeight,
    });
  };

  // Rotation handle functionality
  const handleRotationMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    onInteractionStart();
    setIsRotating(true);

    // Calculate the center of the text box in global viewport coordinates
    // textBox.x/y are relative to image, so add image position (clipRect.left/top)
    const centerX = clipRect.clientLeft + textBox.x + textBox.width / 2;
    const centerY = clipRect.clientTop + textBox.y + contentHeight / 2;

    // Calculate initial angle from center to mouse position
    const initialVectorX = e.clientX - centerX;
    const initialVectorY = e.clientY - centerY;
    const initialAngleRad = Math.atan2(initialVectorX, -initialVectorY);
    const initialAngleDeg = initialAngleRad * (180 / Math.PI);

    setRotationStart({
      x: e.clientX,
      y: e.clientY,
      startRotation: textBox.rotation,
      centerX,
      centerY,
      initialAngle: initialAngleDeg,
    });
  };

  // Mouse move handler
  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;
        const moved = Math.hypot(deltaX, deltaY) > 2;
        if (moved !== isMoving) setIsMoving(moved);

        const proposedX = dragStart.startX + deltaX;
        const proposedY = dragStart.startY + deltaY;

        const SNAP_EPS = 3;
        const containerVerticalGuides = [
          { x: clipRect.left, kind: "container" as const },
          { x: clipRect.left + clipRect.width / 2, kind: "container" as const },
          { x: clipRect.left + clipRect.width, kind: "container" as const },
        ];
        const containerHorizontalGuides = [
          { y: clipRect.top, kind: "container" as const },
          { y: clipRect.top + clipRect.height / 2, kind: "container" as const },
          { y: clipRect.top + clipRect.height, kind: "container" as const },
        ];

        // Peer rects are passed in; include all except self
        const peers = peerRects.filter(() => true);
        const peerVerticalGuides = peers.flatMap((r) => {
          const rotation = r.rotation || 0;
          if (rotation === 0) {
            // No rotation - use simple bounding box guides
            return [
              { x: r.x, kind: "peer" as const },
              { x: r.x + r.width / 2, kind: "peer" as const },
              { x: r.x + r.width, kind: "peer" as const },
            ];
          } else {
            // Rotation - use rotation-aware extremes
            const extremes = getRotatedExtremes(r.x, r.y, r.width, r.height, rotation);
            return [
              { x: extremes.left, kind: "peer" as const },
              { x: extremes.centerX, kind: "peer" as const },
              { x: extremes.right, kind: "peer" as const },
            ];
          }
        });
        const curHeight = liveOuterHeight || textBox.fontSize * 1.2;
        const peerHorizontalGuides = peers.flatMap((r) => {
          const rotation = r.rotation || 0;
          if (rotation === 0) {
            // No rotation - use simple bounding box guides
            return [
              { y: r.y, kind: "peer" as const },
              { y: r.y + r.height / 2, kind: "peer" as const },
              { y: r.y + r.height, kind: "peer" as const },
            ];
          } else {
            // Rotation - use rotation-aware extremes
            const extremes = getRotatedExtremes(r.x, r.y, r.width, r.height, rotation);
            return [
              { y: extremes.top, kind: "peer" as const },
              { y: extremes.centerY, kind: "peer" as const },
              { y: extremes.bottom, kind: "peer" as const },
            ];
          }
        });

        const allV = [...containerVerticalGuides, ...peerVerticalGuides];
        const allH = [...containerHorizontalGuides, ...peerHorizontalGuides];

        // Use rotation-aware extremes for snapping
        const extremes = getRotatedExtremes(
          proposedX,
          proposedY,
          textBox.width,
          curHeight,
          textBox.rotation,
        );

        const curLeft = extremes.left;
        const curCenterX = extremes.centerX;
        const curRight = extremes.right;
        const curTop = extremes.top;
        const curCenterY = extremes.centerY;
        const curBottom = extremes.bottom;

        let bestX = proposedX;
        let vGuide: { x: number; kind: "container" | "peer" } | null = null;
        {
          const candidates: Array<{
            dist: number;
            snapX: number;
            guide: { x: number; kind: "container" | "peer" };
          }> = [];
          for (const g of allV) {
            // For rotated boxes, snap to the extreme points (leftmost, center, rightmost)
            candidates.push({
              dist: Math.abs(g.x - curLeft),
              snapX: g.x - (curLeft - proposedX),
              guide: g,
            });
            candidates.push({
              dist: Math.abs(g.x - curCenterX),
              snapX: g.x - (curCenterX - proposedX),
              guide: g,
            });
            candidates.push({
              dist: Math.abs(g.x - curRight),
              snapX: g.x - (curRight - proposedX),
              guide: g,
            });
          }
          candidates.sort((a, b) => a.dist - b.dist);
          const best = candidates[0];
          if (best && best.dist <= SNAP_EPS) {
            bestX = best.snapX;
            vGuide = { x: best.guide.x, kind: best.guide.kind };
          }
        }

        let bestY = proposedY;
        let hGuide: { y: number; kind: "container" | "peer" } | null = null;
        {
          const candidates: Array<{
            dist: number;
            snapY: number;
            guide: { y: number; kind: "container" | "peer" };
          }> = [];
          for (const g of allH) {
            // For rotated boxes, snap to the extreme points (topmost, center, bottommost)
            candidates.push({
              dist: Math.abs(g.y - curTop),
              snapY: g.y - (curTop - proposedY),
              guide: g,
            });
            candidates.push({
              dist: Math.abs(g.y - curCenterY),
              snapY: g.y - (curCenterY - proposedY),
              guide: g,
            });
            candidates.push({
              dist: Math.abs(g.y - curBottom),
              snapY: g.y - (curBottom - proposedY),
              guide: g,
            });
          }
          candidates.sort((a, b) => a.dist - b.dist);
          const best = candidates[0];
          if (best && best.dist <= SNAP_EPS) {
            bestY = best.snapY;
            hGuide = { y: best.guide.y, kind: best.guide.kind };
          }
        }

        setGuides(vGuide, hGuide);
        onUpdate({ x: bestX, y: bestY });
      } else if (isResizing) {
        const deltaX = e.clientX - resizeStart.x;

        // Build guide candidates for snapping during resize
        const SNAP_EPS = 3; // px
        const containerVerticalGuides = [
          { x: clipRect.left, kind: "container" as const },
          { x: clipRect.left + clipRect.width / 2, kind: "container" as const },
          { x: clipRect.left + clipRect.width, kind: "container" as const },
        ];
        const containerHorizontalGuides = [
          { y: clipRect.top, kind: "container" as const },
          { y: clipRect.top + clipRect.height / 2, kind: "container" as const },
          { y: clipRect.top + clipRect.height, kind: "container" as const },
        ];

        // Peer rects are passed in; include all except self
        const peers = peerRects.filter(() => true);
        const peerVerticalGuides = peers.flatMap((r) => {
          const rotation = r.rotation || 0;
          if (rotation === 0) {
            // No rotation - use simple bounding box guides
            return [
              { x: r.x, kind: "peer" as const },
              { x: r.x + r.width / 2, kind: "peer" as const },
              { x: r.x + r.width, kind: "peer" as const },
            ];
          } else {
            // Rotation - use rotation-aware extremes
            const extremes = getRotatedExtremes(r.x, r.y, r.width, r.height, rotation);
            return [
              { x: extremes.left, kind: "peer" as const },
              { x: extremes.centerX, kind: "peer" as const },
              { x: extremes.right, kind: "peer" as const },
            ];
          }
        });
        const peerHorizontalGuides = peers.flatMap((r) => {
          const rotation = r.rotation || 0;
          if (rotation === 0) {
            // No rotation - use simple bounding box guides
            return [
              { y: r.y, kind: "peer" as const },
              { y: r.y + r.height / 2, kind: "peer" as const },
              { y: r.y + r.height, kind: "peer" as const },
            ];
          } else {
            // Rotation - use rotation-aware extremes
            const extremes = getRotatedExtremes(r.x, r.y, r.width, r.height, rotation);
            return [
              { y: extremes.top, kind: "peer" as const },
              { y: extremes.centerY, kind: "peer" as const },
              { y: extremes.bottom, kind: "peer" as const },
            ];
          }
        });

        const allV = [...containerVerticalGuides, ...peerVerticalGuides];
        const allH = [...containerHorizontalGuides, ...peerHorizontalGuides];

        switch (isResizing) {
          case "se": {
            // Bottom-right corner - diagonal resize with snapping
            const proposedWidth = Math.max(100, resizeStart.width + deltaX);
            const proposedFontSize =
              (resizeStart.fontSize * proposedWidth) / resizeStart.width;
            const proposedHeight = measureTextHeight(
              textBox.text,
              proposedWidth,
              proposedFontSize,
              textBox.fontFamily,
              textBox.isBold,
              textBox.isItalic,
            );

            // Calculate the opposite vertex position that should remain fixed (top-left)
            const fixedX = resizeStart.startX;
            const fixedY = resizeStart.startY;

            // Calculate extreme edges of the rotated box with proposed dimensions
            const proposedExtremes = getRotatedExtremes(
              fixedX,
              fixedY,
              proposedWidth,
              proposedHeight,
              textBox.rotation,
            );

            // Find the best snap point for extreme edges
            let bestSnapDistance = Infinity;
            let bestSnapResult: {
              width: number;
              fontSize: number;
              x: number;
              y: number;
              vGuide: { x: number; kind: "container" | "peer" } | null;
              hGuide: { y: number; kind: "container" | "peer" } | null;
            } | null = null;

            // Check snapping for right extreme edge (vertical guides)
            for (const vg of allV) {
              const distance = Math.abs(vg.x - proposedExtremes.right);
              if (distance <= SNAP_EPS && distance < bestSnapDistance) {
                // For rotation-aware snapping, we need to iteratively find the width
                // that places the right extreme at vg.x while keeping fixed corner stationary
                let testWidth = proposedWidth;
                for (let i = 0; i < 10; i++) {
                  const testFontSize =
                    (resizeStart.fontSize * testWidth) / resizeStart.width;
                  const testHeight = measureTextHeight(
                    textBox.text,
                    testWidth,
                    testFontSize,
                    textBox.fontFamily,
                    textBox.isBold,
                    textBox.isItalic,
                  );
                  const testExtremes = getRotatedExtremes(
                    fixedX,
                    fixedY,
                    testWidth,
                    testHeight,
                    textBox.rotation,
                  );
                  const error = vg.x - testExtremes.right;
                  if (Math.abs(error) < 0.5) break;
                  testWidth += error * 0.5; // Adjust width proportionally
                  if (testWidth < 100) {
                    testWidth = 100;
                    break;
                  }
                }

                if (testWidth >= 100) {
                  const newFontSize =
                    (resizeStart.fontSize * testWidth) / resizeStart.width;
                  bestSnapDistance = distance;
                  bestSnapResult = {
                    width: testWidth,
                    fontSize: newFontSize,
                    x: fixedX,
                    y: fixedY,
                    vGuide: vg,
                    hGuide: null,
                  };
                }
              }
            }

            // Check snapping for bottom extreme edge (horizontal guides)
            for (const hg of allH) {
              const distance = Math.abs(hg.y - proposedExtremes.bottom);
              if (distance <= SNAP_EPS && distance < bestSnapDistance) {
                // Similar iterative approach for bottom edge
                let testWidth = proposedWidth;
                for (let i = 0; i < 10; i++) {
                  const testFontSize =
                    (resizeStart.fontSize * testWidth) / resizeStart.width;
                  const testHeight = measureTextHeight(
                    textBox.text,
                    testWidth,
                    testFontSize,
                    textBox.fontFamily,
                    textBox.isBold,
                    textBox.isItalic,
                  );
                  const testExtremes = getRotatedExtremes(
                    fixedX,
                    fixedY,
                    testWidth,
                    testHeight,
                    textBox.rotation,
                  );
                  const error = hg.y - testExtremes.bottom;
                  if (Math.abs(error) < 0.5) break;
                  // Adjust via height/font size relationship
                  const heightAdjust = error * 0.5;
                  const newFontSize = testFontSize * (1 + heightAdjust / testHeight);
                  testWidth = (newFontSize * resizeStart.width) / resizeStart.fontSize;
                  if (testWidth < 100) {
                    testWidth = 100;
                    break;
                  }
                }

                if (testWidth >= 100) {
                  const newFontSize =
                    (resizeStart.fontSize * testWidth) / resizeStart.width;
                  bestSnapDistance = distance;
                  bestSnapResult = {
                    width: testWidth,
                    fontSize: newFontSize,
                    x: fixedX,
                    y: fixedY,
                    vGuide: null,
                    hGuide: hg,
                  };
                }
              }
            }

            // Apply the best snap result if found, otherwise use proposed values
            if (bestSnapResult) {
              setGuides(bestSnapResult.vGuide, bestSnapResult.hGuide);
              onUpdate({
                width: bestSnapResult.width,
                fontSize: bestSnapResult.fontSize,
                x: bestSnapResult.x,
                y: bestSnapResult.y,
              });
            } else {
              const snappedScale = proposedWidth / resizeStart.width;
              const snappedFontSize = resizeStart.fontSize * snappedScale;
              setGuides(null, null);
              onUpdate({
                width: proposedWidth,
                fontSize: snappedFontSize,
                x: fixedX,
                y: fixedY,
              });
            }
            break;
          }
          case "sw": {
            // Bottom-left corner - diagonal resize with snapping
            const proposedWidth = Math.max(100, resizeStart.width - deltaX);
            const proposedFontSize =
              (resizeStart.fontSize * proposedWidth) / resizeStart.width;
            const proposedHeight = measureTextHeight(
              textBox.text,
              proposedWidth,
              proposedFontSize,
              textBox.fontFamily,
              textBox.isBold,
              textBox.isItalic,
            );

            // Calculate the opposite vertex position that should remain fixed (top-right)
            const fixedX = resizeStart.startX + resizeStart.width;
            const fixedY = resizeStart.startY;
            const proposedX = fixedX - proposedWidth;

            // Calculate extreme edges of the rotated box with proposed dimensions
            const proposedExtremes = getRotatedExtremes(
              proposedX,
              fixedY,
              proposedWidth,
              proposedHeight,
              textBox.rotation,
            );

            // Find the best snap point for extreme edges
            let bestSnapDistance = Infinity;
            let bestSnapResult: {
              width: number;
              fontSize: number;
              x: number;
              y: number;
              vGuide: { x: number; kind: "container" | "peer" } | null;
              hGuide: { y: number; kind: "container" | "peer" } | null;
            } | null = null;

            // Check snapping for left extreme edge (vertical guides)
            for (const vg of allV) {
              const distance = Math.abs(vg.x - proposedExtremes.left);
              if (distance <= SNAP_EPS && distance < bestSnapDistance) {
                let testWidth = proposedWidth;
                for (let i = 0; i < 10; i++) {
                  const testFontSize =
                    (resizeStart.fontSize * testWidth) / resizeStart.width;
                  const testHeight = measureTextHeight(
                    textBox.text,
                    testWidth,
                    testFontSize,
                    textBox.fontFamily,
                    textBox.isBold,
                    textBox.isItalic,
                  );
                  const testX = fixedX - testWidth;
                  const testExtremes = getRotatedExtremes(
                    testX,
                    fixedY,
                    testWidth,
                    testHeight,
                    textBox.rotation,
                  );
                  const error = vg.x - testExtremes.left;
                  if (Math.abs(error) < 0.5) break;
                  testWidth -= error * 0.5;
                  if (testWidth < 100) {
                    testWidth = 100;
                    break;
                  }
                }

                if (testWidth >= 100) {
                  const newFontSize =
                    (resizeStart.fontSize * testWidth) / resizeStart.width;
                  bestSnapDistance = distance;
                  bestSnapResult = {
                    width: testWidth,
                    fontSize: newFontSize,
                    x: fixedX - testWidth,
                    y: fixedY,
                    vGuide: vg,
                    hGuide: null,
                  };
                }
              }
            }

            // Check snapping for bottom extreme edge (horizontal guides)
            for (const hg of allH) {
              const distance = Math.abs(hg.y - proposedExtremes.bottom);
              if (distance <= SNAP_EPS && distance < bestSnapDistance) {
                let testWidth = proposedWidth;
                for (let i = 0; i < 10; i++) {
                  const testFontSize =
                    (resizeStart.fontSize * testWidth) / resizeStart.width;
                  const testHeight = measureTextHeight(
                    textBox.text,
                    testWidth,
                    testFontSize,
                    textBox.fontFamily,
                    textBox.isBold,
                    textBox.isItalic,
                  );
                  const testX = fixedX - testWidth;
                  const testExtremes = getRotatedExtremes(
                    testX,
                    fixedY,
                    testWidth,
                    testHeight,
                    textBox.rotation,
                  );
                  const error = hg.y - testExtremes.bottom;
                  if (Math.abs(error) < 0.5) break;
                  const heightAdjust = error * 0.5;
                  const newFontSize = testFontSize * (1 + heightAdjust / testHeight);
                  testWidth = (newFontSize * resizeStart.width) / resizeStart.fontSize;
                  if (testWidth < 100) {
                    testWidth = 100;
                    break;
                  }
                }

                if (testWidth >= 100) {
                  const newFontSize =
                    (resizeStart.fontSize * testWidth) / resizeStart.width;
                  bestSnapDistance = distance;
                  bestSnapResult = {
                    width: testWidth,
                    fontSize: newFontSize,
                    x: fixedX - testWidth,
                    y: fixedY,
                    vGuide: null,
                    hGuide: hg,
                  };
                }
              }
            }

            // Apply the best snap result if found, otherwise use proposed values
            if (bestSnapResult) {
              setGuides(bestSnapResult.vGuide, bestSnapResult.hGuide);
              onUpdate({
                width: bestSnapResult.width,
                fontSize: bestSnapResult.fontSize,
                x: bestSnapResult.x,
                y: bestSnapResult.y,
              });
            } else {
              const snappedScale = proposedWidth / resizeStart.width;
              const snappedFontSize = resizeStart.fontSize * snappedScale;
              setGuides(null, null);
              onUpdate({
                width: proposedWidth,
                fontSize: snappedFontSize,
                x: proposedX,
                y: fixedY,
              });
            }
            break;
          }
          case "ne": {
            // Top-right corner - diagonal resize with snapping
            const proposedWidth = Math.max(100, resizeStart.width + deltaX);
            const proposedFontSize =
              (resizeStart.fontSize * proposedWidth) / resizeStart.width;
            const proposedHeight = measureTextHeight(
              textBox.text,
              proposedWidth,
              proposedFontSize,
              textBox.fontFamily,
              textBox.isBold,
              textBox.isItalic,
            );

            // Calculate the opposite vertex position that should remain fixed (bottom-left)
            const fixedX = resizeStart.startX;
            const fixedY = resizeStart.startY + resizeStart.startContentHeight;
            const proposedY = fixedY - proposedHeight;

            // Calculate extreme edges of the rotated box with proposed dimensions
            const proposedExtremes = getRotatedExtremes(
              fixedX,
              proposedY,
              proposedWidth,
              proposedHeight,
              textBox.rotation,
            );

            // Find the best snap point for extreme edges
            let bestSnapDistance = Infinity;
            let bestSnapResult: {
              width: number;
              fontSize: number;
              x: number;
              y: number;
              vGuide: { x: number; kind: "container" | "peer" } | null;
              hGuide: { y: number; kind: "container" | "peer" } | null;
            } | null = null;

            // Check snapping for right extreme edge (vertical guides)
            for (const vg of allV) {
              const distance = Math.abs(vg.x - proposedExtremes.right);
              if (distance <= SNAP_EPS && distance < bestSnapDistance) {
                let testWidth = proposedWidth;
                for (let i = 0; i < 10; i++) {
                  const testFontSize =
                    (resizeStart.fontSize * testWidth) / resizeStart.width;
                  const testHeight = measureTextHeight(
                    textBox.text,
                    testWidth,
                    testFontSize,
                    textBox.fontFamily,
                    textBox.isBold,
                    textBox.isItalic,
                  );
                  const testY = fixedY - testHeight;
                  const testExtremes = getRotatedExtremes(
                    fixedX,
                    testY,
                    testWidth,
                    testHeight,
                    textBox.rotation,
                  );
                  const error = vg.x - testExtremes.right;
                  if (Math.abs(error) < 0.5) break;
                  testWidth += error * 0.5;
                  if (testWidth < 100) {
                    testWidth = 100;
                    break;
                  }
                }

                if (testWidth >= 100) {
                  const newFontSize =
                    (resizeStart.fontSize * testWidth) / resizeStart.width;
                  const newHeight = measureTextHeight(
                    textBox.text,
                    testWidth,
                    newFontSize,
                    textBox.fontFamily,
                    textBox.isBold,
                    textBox.isItalic,
                  );
                  bestSnapDistance = distance;
                  bestSnapResult = {
                    width: testWidth,
                    fontSize: newFontSize,
                    x: fixedX,
                    y: fixedY - newHeight,
                    vGuide: vg,
                    hGuide: null,
                  };
                }
              }
            }

            // Check snapping for top extreme edge (horizontal guides)
            for (const hg of allH) {
              const distance = Math.abs(hg.y - proposedExtremes.top);
              if (distance <= SNAP_EPS && distance < bestSnapDistance) {
                let testWidth = proposedWidth;
                for (let i = 0; i < 10; i++) {
                  const testFontSize =
                    (resizeStart.fontSize * testWidth) / resizeStart.width;
                  const testHeight = measureTextHeight(
                    textBox.text,
                    testWidth,
                    testFontSize,
                    textBox.fontFamily,
                    textBox.isBold,
                    textBox.isItalic,
                  );
                  const testY = fixedY - testHeight;
                  const testExtremes = getRotatedExtremes(
                    fixedX,
                    testY,
                    testWidth,
                    testHeight,
                    textBox.rotation,
                  );
                  const error = hg.y - testExtremes.top;
                  if (Math.abs(error) < 0.5) break;
                  const heightAdjust = -error * 0.5;
                  const newFontSize = testFontSize * (1 + heightAdjust / testHeight);
                  testWidth = (newFontSize * resizeStart.width) / resizeStart.fontSize;
                  if (testWidth < 100) {
                    testWidth = 100;
                    break;
                  }
                }

                if (testWidth >= 100) {
                  const newFontSize =
                    (resizeStart.fontSize * testWidth) / resizeStart.width;
                  const newHeight = measureTextHeight(
                    textBox.text,
                    testWidth,
                    newFontSize,
                    textBox.fontFamily,
                    textBox.isBold,
                    textBox.isItalic,
                  );
                  bestSnapDistance = distance;
                  bestSnapResult = {
                    width: testWidth,
                    fontSize: newFontSize,
                    x: fixedX,
                    y: fixedY - newHeight,
                    vGuide: null,
                    hGuide: hg,
                  };
                }
              }
            }

            // Apply the best snap result if found, otherwise use proposed values
            if (bestSnapResult) {
              setGuides(bestSnapResult.vGuide, bestSnapResult.hGuide);
              onUpdate({
                width: bestSnapResult.width,
                fontSize: bestSnapResult.fontSize,
                x: bestSnapResult.x,
                y: bestSnapResult.y,
              });
            } else {
              const snappedScale = proposedWidth / resizeStart.width;
              const snappedFontSize = resizeStart.fontSize * snappedScale;
              const snappedHeight = measureTextHeight(
                textBox.text,
                proposedWidth,
                snappedFontSize,
                textBox.fontFamily,
                textBox.isBold,
                textBox.isItalic,
              );
              setGuides(null, null);
              onUpdate({
                width: proposedWidth,
                fontSize: snappedFontSize,
                x: fixedX,
                y: fixedY - snappedHeight,
              });
            }
            break;
          }
          case "nw": {
            // Top-left corner - diagonal resize with snapping
            const proposedWidth = Math.max(100, resizeStart.width - deltaX);
            const proposedFontSize =
              (resizeStart.fontSize * proposedWidth) / resizeStart.width;
            const proposedHeight = measureTextHeight(
              textBox.text,
              proposedWidth,
              proposedFontSize,
              textBox.fontFamily,
              textBox.isBold,
              textBox.isItalic,
            );

            // Calculate the opposite vertex position that should remain fixed (bottom-right)
            const fixedX = resizeStart.startX + resizeStart.width;
            const fixedY = resizeStart.startY + resizeStart.startContentHeight;
            const proposedX = fixedX - proposedWidth;
            const proposedY = fixedY - proposedHeight;

            // Calculate extreme edges of the rotated box with proposed dimensions
            const proposedExtremes = getRotatedExtremes(
              proposedX,
              proposedY,
              proposedWidth,
              proposedHeight,
              textBox.rotation,
            );

            // Find the best snap point for extreme edges
            let bestSnapDistance = Infinity;
            let bestSnapResult: {
              width: number;
              fontSize: number;
              x: number;
              y: number;
              vGuide: { x: number; kind: "container" | "peer" } | null;
              hGuide: { y: number; kind: "container" | "peer" } | null;
            } | null = null;

            // Check snapping for left extreme edge (vertical guides)
            for (const vg of allV) {
              const distance = Math.abs(vg.x - proposedExtremes.left);
              if (distance <= SNAP_EPS && distance < bestSnapDistance) {
                let testWidth = proposedWidth;
                for (let i = 0; i < 10; i++) {
                  const testFontSize =
                    (resizeStart.fontSize * testWidth) / resizeStart.width;
                  const testHeight = measureTextHeight(
                    textBox.text,
                    testWidth,
                    testFontSize,
                    textBox.fontFamily,
                    textBox.isBold,
                    textBox.isItalic,
                  );
                  const testX = fixedX - testWidth;
                  const testY = fixedY - testHeight;
                  const testExtremes = getRotatedExtremes(
                    testX,
                    testY,
                    testWidth,
                    testHeight,
                    textBox.rotation,
                  );
                  const error = vg.x - testExtremes.left;
                  if (Math.abs(error) < 0.5) break;
                  testWidth -= error * 0.5;
                  if (testWidth < 100) {
                    testWidth = 100;
                    break;
                  }
                }

                if (testWidth >= 100) {
                  const newFontSize =
                    (resizeStart.fontSize * testWidth) / resizeStart.width;
                  const newHeight = measureTextHeight(
                    textBox.text,
                    testWidth,
                    newFontSize,
                    textBox.fontFamily,
                    textBox.isBold,
                    textBox.isItalic,
                  );
                  bestSnapDistance = distance;
                  bestSnapResult = {
                    width: testWidth,
                    fontSize: newFontSize,
                    x: fixedX - testWidth,
                    y: fixedY - newHeight,
                    vGuide: vg,
                    hGuide: null,
                  };
                }
              }
            }

            // Check snapping for top extreme edge (horizontal guides)
            for (const hg of allH) {
              const distance = Math.abs(hg.y - proposedExtremes.top);
              if (distance <= SNAP_EPS && distance < bestSnapDistance) {
                let testWidth = proposedWidth;
                for (let i = 0; i < 10; i++) {
                  const testFontSize =
                    (resizeStart.fontSize * testWidth) / resizeStart.width;
                  const testHeight = measureTextHeight(
                    textBox.text,
                    testWidth,
                    testFontSize,
                    textBox.fontFamily,
                    textBox.isBold,
                    textBox.isItalic,
                  );
                  const testX = fixedX - testWidth;
                  const testY = fixedY - testHeight;
                  const testExtremes = getRotatedExtremes(
                    testX,
                    testY,
                    testWidth,
                    testHeight,
                    textBox.rotation,
                  );
                  const error = hg.y - testExtremes.top;
                  if (Math.abs(error) < 0.5) break;
                  const heightAdjust = -error * 0.5;
                  const newFontSize = testFontSize * (1 + heightAdjust / testHeight);
                  testWidth = (newFontSize * resizeStart.width) / resizeStart.fontSize;
                  if (testWidth < 100) {
                    testWidth = 100;
                    break;
                  }
                }

                if (testWidth >= 100) {
                  const newFontSize =
                    (resizeStart.fontSize * testWidth) / resizeStart.width;
                  const newHeight = measureTextHeight(
                    textBox.text,
                    testWidth,
                    newFontSize,
                    textBox.fontFamily,
                    textBox.isBold,
                    textBox.isItalic,
                  );
                  bestSnapDistance = distance;
                  bestSnapResult = {
                    width: testWidth,
                    fontSize: newFontSize,
                    x: fixedX - testWidth,
                    y: fixedY - newHeight,
                    vGuide: null,
                    hGuide: hg,
                  };
                }
              }
            }

            // Apply the best snap result if found, otherwise use proposed values
            if (bestSnapResult) {
              setGuides(bestSnapResult.vGuide, bestSnapResult.hGuide);
              onUpdate({
                width: bestSnapResult.width,
                fontSize: bestSnapResult.fontSize,
                x: bestSnapResult.x,
                y: bestSnapResult.y,
              });
            } else {
              const snappedScale = proposedWidth / resizeStart.width;
              const snappedFontSize = resizeStart.fontSize * snappedScale;
              const snappedHeight = measureTextHeight(
                textBox.text,
                proposedWidth,
                snappedFontSize,
                textBox.fontFamily,
                textBox.isBold,
                textBox.isItalic,
              );
              setGuides(null, null);
              onUpdate({
                width: proposedWidth,
                fontSize: snappedFontSize,
                x: proposedX,
                y: fixedY - snappedHeight,
              });
            }
            break;
          }
          case "e": {
            // Right side - resize width only
            const proposedWidth = Math.max(100, resizeStart.width + deltaX);
            const currentHeight = liveOuterHeight || textBox.fontSize * 1.2;

            // Calculate extreme edges with proposed width
            const proposedExtremes = getRotatedExtremes(
              resizeStart.startX,
              resizeStart.startY,
              proposedWidth,
              currentHeight,
              textBox.rotation,
            );

            // Find closest snap for right extreme edge
            let bestDist = Infinity;
            let bestGuide: { x: number; kind: "container" | "peer" } | null = null;
            let bestWidth = proposedWidth;

            for (const g of allV) {
              const dist = Math.abs(g.x - proposedExtremes.right);
              if (dist <= SNAP_EPS && dist < bestDist) {
                // Iteratively find width that places right edge at g.x
                let testWidth = proposedWidth;
                for (let i = 0; i < 10; i++) {
                  const testExtremes = getRotatedExtremes(
                    resizeStart.startX,
                    resizeStart.startY,
                    testWidth,
                    currentHeight,
                    textBox.rotation,
                  );
                  const error = g.x - testExtremes.right;
                  if (Math.abs(error) < 0.5) break;
                  testWidth += error * 0.5;
                  if (testWidth < 100) {
                    testWidth = 100;
                    break;
                  }
                }
                if (testWidth >= 100) {
                  bestDist = dist;
                  bestWidth = testWidth;
                  bestGuide = { x: g.x, kind: g.kind };
                }
              }
            }

            setGuides(bestGuide, null);
            onUpdate({ width: bestWidth });
            break;
          }
          case "w": {
            // Left side - resize width and move position to keep right edge fixed
            const proposedWidth = Math.max(100, resizeStart.width - deltaX);
            const currentHeight = liveOuterHeight || textBox.fontSize * 1.2;
            const fixedRightX = resizeStart.startX + resizeStart.width;
            const proposedX = fixedRightX - proposedWidth;

            // Calculate extreme edges with proposed width
            const proposedExtremes = getRotatedExtremes(
              proposedX,
              resizeStart.startY,
              proposedWidth,
              currentHeight,
              textBox.rotation,
            );

            // Find closest snap for left extreme edge
            let bestDist = Infinity;
            let bestGuide: { x: number; kind: "container" | "peer" } | null = null;
            let bestWidth = proposedWidth;
            let bestX = proposedX;

            for (const g of allV) {
              const dist = Math.abs(g.x - proposedExtremes.left);
              if (dist <= SNAP_EPS && dist < bestDist) {
                // Iteratively find width that places left edge at g.x
                let testWidth = proposedWidth;
                for (let i = 0; i < 10; i++) {
                  const testX = fixedRightX - testWidth;
                  const testExtremes = getRotatedExtremes(
                    testX,
                    resizeStart.startY,
                    testWidth,
                    currentHeight,
                    textBox.rotation,
                  );
                  const error = g.x - testExtremes.left;
                  if (Math.abs(error) < 0.5) break;
                  testWidth -= error * 0.5;
                  if (testWidth < 100) {
                    testWidth = 100;
                    break;
                  }
                }
                if (testWidth >= 100) {
                  bestDist = dist;
                  bestWidth = testWidth;
                  bestX = fixedRightX - testWidth;
                  bestGuide = { x: g.x, kind: g.kind };
                }
              }
            }

            setGuides(bestGuide, null);
            onUpdate({ width: bestWidth, x: bestX });
            break;
          }
        }
      } else if (isRotating) {
        // Calculate rotation based on angle from vertical line (north = 0°)
        const currentX = e.clientX;
        const currentY = e.clientY;

        // Calculate vector from image center to current mouse position
        const vectorX = currentX - rotationStart.centerX;
        const vectorY = currentY - rotationStart.centerY;

        // Calculate angle relative to vertical line (north = 0°)
        // atan2 gives angle from horizontal, so we need to adjust for vertical reference
        // -90° adjustment to make vertical line (0, -1) = 0°
        const currentAngleRad = Math.atan2(vectorX, -vectorY);
        const currentAngleDeg = currentAngleRad * (180 / Math.PI);

        // Calculate the rotation delta from the initial angle
        let rotationDelta = currentAngleDeg - rotationStart.initialAngle;

        // Normalize to [-180, 180]
        while (rotationDelta > 180) rotationDelta -= 360;
        while (rotationDelta < -180) rotationDelta += 360;

        // Apply rotation with 1-degree increments for precise control
        const snapIncrement = 1;
        const snappedRotationDelta =
          Math.round(rotationDelta / snapIncrement) * snapIncrement;

        let newRotation = rotationStart.startRotation + snappedRotationDelta;

        // Normalize final rotation to be between -180 and 180 degrees
        while (newRotation > 180) newRotation -= 360;
        while (newRotation < -180) newRotation += 360;

        onUpdate({ rotation: newRotation });
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      setIsDragging(false);
      setIsResizing(null);
      setIsRotating(false);
      setIsMoving(false);
      lastDragEndedAtRef.current = Date.now();
      // If pointer moved more than a small threshold during drag, suppress immediate edit
      if (isDragging) {
        const moved = Math.hypot(e.clientX - dragStart.x, e.clientY - dragStart.y);
        if (moved > 3) {
          wasDragRecentlyRef.current = true;
          setTimeout(() => {
            wasDragRecentlyRef.current = false;
          }, 250);
        }
      }
      // Notify parent so it can suppress next overlay click
      onInteractionEnd();
      setGuides(null, null);

      // Additional safety: prevent any immediate click events on the overlay
      // This helps when mouse up happens outside the text box
      setTimeout(() => {
        const overlay = document.querySelector("[data-text-overlay]");
        if (overlay) {
          const preventClick = (e: Event) => {
            e.stopPropagation();
            e.preventDefault();
          };
          overlay.addEventListener("click", preventClick, { once: true, capture: true });
          // Remove the listener after a short delay if it wasn't triggered
          setTimeout(() => {
            overlay.removeEventListener("click", preventClick, { capture: true });
          }, 50);
        }
      }, 0);
    };

    if (isDragging || isResizing || isRotating) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      // Create a global cursor override using CSS
      let cursorStyle = "default";
      if (isDragging) {
        cursorStyle = "move";
      } else if (isRotating) {
        cursorStyle = "grab";
      } else if (isResizing) {
        const cursorMap: Record<string, string> = {
          e: "ew-resize",
          w: "ew-resize",
          se: "nwse-resize",
          nw: "nwse-resize",
          ne: "nesw-resize",
          sw: "nesw-resize",
        };
        cursorStyle = cursorMap[isResizing] || "default";
      }

      // Add a global CSS rule that overrides all cursor styles
      const styleId = "global-cursor-override";
      let styleElement = document.getElementById(styleId) as HTMLStyleElement;
      if (!styleElement) {
        styleElement = document.createElement("style");
        styleElement.id = styleId;
        document.head.appendChild(styleElement);
      }
      styleElement.textContent = `
        *, *::before, *::after {
          cursor: ${cursorStyle} !important;
        }
      `;

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        // Remove the global cursor override
        const styleElement = document.getElementById(styleId);
        if (styleElement) {
          styleElement.remove();
        }
      };
    }
  }, [
    isDragging,
    isResizing,
    isRotating,
    isMoving,
    dragStart,
    resizeStart,
    rotationStart,
    textBox,
    onUpdate,
    measureTextHeight,
    onInteractionEnd,
    clipRect.left,
    clipRect.top,
    clipRect.width,
    clipRect.height,
    liveOuterHeight,
    peerRects,
    setGuides,
  ]);

  // compute content height so border/handles size matches content while we render text in clipped layer only
  const contentHeight = React.useMemo(() => {
    const lineHeightPx = textBox.fontSize * 1.2;
    // During active resizing, prefer synchronous measurement to avoid a 1-frame lag vs. baseline math
    if (isResizing) {
      const measured = measureTextHeight(
        textBox.text,
        textBox.width,
        textBox.fontSize,
        textBox.fontFamily,
        textBox.isBold,
        textBox.isItalic,
      );
      return Math.max(lineHeightPx, measured);
    }
    if (typeof liveOuterHeight === "number")
      return Math.max(lineHeightPx, liveOuterHeight);
    const measured = measureTextHeight(
      textBox.text,
      textBox.width,
      textBox.fontSize,
      textBox.fontFamily,
      textBox.isBold,
      textBox.isItalic,
    );
    return Math.max(lineHeightPx, measured);
  }, [
    isResizing,
    liveOuterHeight,
    textBox.text,
    textBox.width,
    textBox.fontSize,
    textBox.fontFamily,
    textBox.isBold,
    textBox.isItalic,
    measureTextHeight,
  ]);

  return (
    <div
      ref={containerRef}
      data-text-box-id={textBox.id}
      style={{
        position: "absolute",
        left: `${textBox.x}px`,
        top: `${textBox.y}px`,
        width: `${textBox.width}px`,
        height: `${contentHeight}px`,
        fontSize: `${textBox.fontSize}px`,
        fontFamily: textBox.fontFamily,
        color: textBox.fill,
        fontWeight: textBox.isBold ? "700" : "400",
        fontStyle: textBox.isItalic ? "italic" : "normal",
        textDecoration:
          `${textBox.isUnderline ? "underline " : ""}${textBox.isStrikethrough ? " line-through" : ""}`.trim(),
        textAlign: textBox.textAlign,
        transform: `rotate(${textBox.rotation}deg)`,
        outline: textBox.isSelected
          ? "2px solid #7C3AED"
          : isHovered && !textBox.isEditing
            ? "1px solid #7C3AED"
            : "2px solid transparent",
        outlineOffset: "-1px",
        border: "none",
        cursor: textBox.isEditing ? "text" : "move",
        minHeight: `${textBox.fontSize * 1.2}px`,
        display: "flex",
        alignItems: "flex-start",
        padding: 0,
        background: "transparent",
        userSelect: "none",
        // keep selected above editing text layer so handles remain usable
        zIndex: textBox.isSelected ? 49 : 2,
        // Allow clicks to reach textarea while editing; handles/toolbar have pointerEvents:auto
        pointerEvents: textBox.isEditing ? "none" : "auto",
      }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        leftRecentlyRef.current = true;
        setTimeout(() => {
          leftRecentlyRef.current = false;
        }, 150);
      }}
    >
      {/* text is rendered in clipped layer below to avoid duplication */}

      {/* Selection handles (visible during selected and editing) */}
      {textBox.isSelected && (
        <>
          {/* Floating action bar */}
          {!isMoving &&
            !isResizing &&
            (() => {
              // Check if text box is near the top edge of the image
              const isNearTopEdge = textBox.y < 50; // 60px threshold from top
              const isNearBottomEdge = textBox.y + contentHeight > clipRect.height - 50; // 60px threshold from bottom
              // For rotations > 90 degrees, reverse the logic since the text box is visually upside down
              const isRotatedMoreThan90 = Math.abs(textBox.rotation) > 90;
              const shouldShowBelow =
                (isNearTopEdge && !isRotatedMoreThan90) ||
                (isNearBottomEdge && isRotatedMoreThan90);

              const buttonBarTop = shouldShowBelow ? `${contentHeight + 4}px` : "-44px";

              return (
                <div
                  style={{
                    position: "absolute",
                    top: buttonBarTop,
                    left: "50%",
                    transform: "translateX(-50%)",
                    display: "flex",
                    gap: "8px",
                    background: "white",
                    borderRadius: "12px",
                    padding: "6px 8px",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
                    border: "1px solid rgba(0,0,0,0.06)",
                    zIndex: 200,
                    color: "#0A0A0A",
                    pointerEvents: "auto",
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    title="Duplicate"
                    onClick={onDuplicate}
                    style={{
                      width: 28,
                      height: 28,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 8,
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}
                  >
                    <IconCopy size={16} />
                  </button>
                  <button
                    title="Delete"
                    onClick={onDelete}
                    style={{
                      width: 28,
                      height: 28,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 8,
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}
                  >
                    <IconTrash size={16} />
                  </button>
                </div>
              );
            })()}
          {/* Corner handles - conditionally visible based on size */}

          <>
            <div
              style={{
                position: "absolute",
                top: `${CORNER_OFFSET_PX}px`,
                left: `${CORNER_OFFSET_PX}px`,
                width: `${CORNER_SIZE}px`,
                height: `${CORNER_SIZE}px`,
                background: "#FFFFFF",
                border: "2px solid #7C3AED",
                borderRadius: "50%",
                boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                cursor: "nw-resize",
                zIndex: 20,
                pointerEvents: "auto",
              }}
              onMouseDown={(e) => handleResizeMouseDown(e, "nw")}
            />
            {shouldShowCornerHandles() && (
              <>
                <div
                  style={{
                    position: "absolute",
                    top: `${CORNER_OFFSET_PX}px`,
                    right: `${CORNER_OFFSET_PX}px`,
                    width: `${CORNER_SIZE}px`,
                    height: `${CORNER_SIZE}px`,
                    background: "#FFFFFF",
                    border: "2px solid #7C3AED",
                    borderRadius: "50%",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                    cursor: "ne-resize",
                    zIndex: 20,
                    pointerEvents: "auto",
                  }}
                  onMouseDown={(e) => handleResizeMouseDown(e, "ne")}
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: `${CORNER_OFFSET_PX}px`,
                    left: `${CORNER_OFFSET_PX}px`,
                    width: `${CORNER_SIZE}px`,
                    height: `${CORNER_SIZE}px`,
                    background: "#FFFFFF",
                    border: "2px solid #7C3AED",
                    borderRadius: "50%",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                    cursor: "sw-resize",
                    zIndex: 20,
                    pointerEvents: "auto",
                  }}
                  onMouseDown={(e) => handleResizeMouseDown(e, "sw")}
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: `${CORNER_OFFSET_PX}px`,
                    right: `${CORNER_OFFSET_PX}px`,
                    width: `${CORNER_SIZE}px`,
                    height: `${CORNER_SIZE}px`,
                    background: "#FFFFFF",
                    border: "2px solid #7C3AED",
                    borderRadius: "50%",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                    cursor: "se-resize",
                    zIndex: 20,
                    pointerEvents: "auto",
                  }}
                  onMouseDown={(e) => handleResizeMouseDown(e, "se")}
                />
              </>
            )}
          </>

          {/* Side handles - conditionally visible based on size */}

          <>
            {/* Right-side pill handle - width adjustment */}
            <div
              style={{
                position: "absolute",
                top: "50%",
                right: `${SIDE_OFFSET_PX}px`,
                transform: "translateY(-50%)",
                width: `${SIDE_WIDTH}px`,
                height: `${SIDE_HEIGHT}px`,
                background: "#FFFFFF",
                border: "2px solid #7C3AED",
                borderRadius: "6px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                cursor: "e-resize",
                zIndex: 20,
                pointerEvents: "auto",
              }}
              onMouseDown={(e) => handleResizeMouseDown(e, "e")}
            />
            {shouldShowSideHandles() && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: `${SIDE_OFFSET_PX}px`,
                  transform: "translateY(-50%)",
                  width: `${SIDE_WIDTH}px`,
                  height: `${SIDE_HEIGHT}px`,
                  background: "#FFFFFF",
                  border: "2px solid #7C3AED",
                  borderRadius: "6px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                  cursor: "w-resize",
                  zIndex: 20,
                  pointerEvents: "auto",
                }}
                onMouseDown={(e) => handleResizeMouseDown(e, "w")}
              />
            )}
          </>

          {/* Invisible resize zones for when handles are not visible - maintains functionality */}
          {!shouldShowCornerHandles() && (
            <>
              {/* Invisible corner resize zones */}
              <div
                style={{
                  position: "absolute",
                  top: "-5px",
                  left: "-5px",
                  width: "20px",
                  height: "20px",
                  cursor: "nw-resize",
                  zIndex: 20,
                  pointerEvents: "auto",
                }}
                onMouseDown={(e) => handleResizeMouseDown(e, "nw")}
              />
              <div
                style={{
                  position: "absolute",
                  top: "-5px",
                  right: "-5px",
                  width: "20px",
                  height: "20px",
                  cursor: "ne-resize",
                  zIndex: 20,
                  pointerEvents: "auto",
                }}
                onMouseDown={(e) => handleResizeMouseDown(e, "ne")}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "-5px",
                  left: "-5px",
                  width: "20px",
                  height: "20px",
                  cursor: "sw-resize",
                  zIndex: 20,
                  pointerEvents: "auto",
                }}
                onMouseDown={(e) => handleResizeMouseDown(e, "sw")}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "-5px",
                  right: "-5px",
                  width: "20px",
                  height: "20px",
                  cursor: "se-resize",
                  zIndex: 20,
                  pointerEvents: "auto",
                }}
                onMouseDown={(e) => handleResizeMouseDown(e, "se")}
              />
            </>
          )}

          {!shouldShowSideHandles() && (
            <>
              {/* Invisible side resize zones */}
              <div
                style={{
                  position: "absolute",
                  top: "0",
                  right: "-5px",
                  width: "10px",
                  height: "100%",
                  cursor: "e-resize",
                  zIndex: 20,
                  pointerEvents: "auto",
                }}
                onMouseDown={(e) => handleResizeMouseDown(e, "e")}
              />
              <div
                style={{
                  position: "absolute",
                  top: "0",
                  left: "-5px",
                  width: "10px",
                  height: "100%",
                  cursor: "w-resize",
                  zIndex: 20,
                  pointerEvents: "auto",
                }}
                onMouseDown={(e) => handleResizeMouseDown(e, "w")}
              />
            </>
          )}

          {/* Rotation control button - positioned below the text box or button bar */}
          {!isMoving &&
            !isResizing &&
            (() => {
              // Check if text box is near the top edge of the image
              const isNearTopEdge = textBox.y < 50; // 60px threshold from top
              const isNearBottomEdge = textBox.y + contentHeight > clipRect.height - 50; // 60px threshold from bottom
              // For rotations > 90 degrees, reverse the logic since the text box is visually upside down
              const isRotatedMoreThan90 = Math.abs(textBox.rotation) > 90;
              const shouldShowBelow =
                (isNearTopEdge && !isRotatedMoreThan90) ||
                (isNearBottomEdge && isRotatedMoreThan90);
              const rotationButtonTop = shouldShowBelow
                ? `${contentHeight + 50}px`
                : `${contentHeight + 10}px`;

              return (
                <div
                  style={{
                    position: "absolute",
                    top: rotationButtonTop,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "28px",
                    height: "28px",
                    background: "#7C3AED",
                    borderRadius: "50%",
                    border: "1px solid #FFFFFF",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    cursor: "grab",
                    zIndex: 20,
                    pointerEvents: "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseDown={handleRotationMouseDown}
                >
                  {/* Show degree value during rotation, otherwise show rotation icon */}
                  {isRotating ? (
                    <span
                      style={{
                        color: "white",
                        fontSize: "10px",
                        fontWeight: "bold",
                        fontFamily: "monospace",
                      }}
                    >
                      {Math.round(textBox.rotation)}°
                    </span>
                  ) : (
                    <RiLoopRightLine size={14} className="text-white" />
                  )}
                </div>
              );
            })()}
        </>
      )}
    </div>
  );
};

export default KonvaTextBox;
export type { TextBox };
